import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Result } from '@/domain/protocols/Result'
import { ConvertedChartData, UserChartBounds } from '@/domain/protocols/UserChart'

import { setPendingOriginalUpload, setPendingProcessedUpload } from '@/data/UserChartRepository/userChartTilesOutbox'
import { registerUserChartTiles, userChartTilesUrlTemplate } from '@/data/UserChartRepository/userChartTileStore'
import { extractTilesFromZip } from '@/data/UserChartRepository/userChartTilesZip'

import { PILOTAPP_BFF_BASE } from '../apiBaseUrls'
import type { HttpResponse, HttpUploadProgressEvent, IHttpClient } from '../IHttpClient'
import { ConvertChartRequest, IChartConverterWebClient } from './IChartConverterWebClient'

import { calculateBoundsFromTileKeys } from '@/utils/tileBounds'

// Ceiling for the *server processing* phase (counted from the moment the upload
// reaches 100%), per file type. The upload phase itself is not bounded by an
// absolute timeout — see the activity watchdog below.
export const CHART_CONVERTER_TIMEOUT_PDF_TIFF_MS = 300000
export const CHART_CONVERTER_TIMEOUT_MBTILES_MS = 1800000
// Abort the upload only when it truly stalls: no progress event for this long.
// A slow-but-progressing connection (e.g. slow 4G) keeps emitting progress and is
// never aborted, however long it takes.
export const CHART_CONVERTER_UPLOAD_STALL_TIMEOUT_MS = 60000
export const CHART_CONVERTER_WATCHDOG_INTERVAL_MS = 5000

const MBTILES_EXTENSION_REGEX = /\.mbtiles$/i

type ChartConverterErrorBody = { error?: string }

export type ConverterAbortReason = 'stall' | 'processing' | null

export interface ConverterWatchdogState {
  // Whether the upload reached 100% (server is now processing).
  uploadComplete: boolean
  // Timestamp (ms) of the last upload progress event.
  lastActivityAt: number
  // Timestamp (ms) when the upload reached 100%.
  uploadCompletedAt: number
  // Max idle time during upload before treating the connection as stalled.
  stallMs: number
  // Max server processing time after the upload completes.
  processingMs: number
}

/**
 * Decides whether the converter request should be aborted given the current
 * watchdog state and clock. During the upload phase it aborts only on inactivity
 * (`stall`); after the upload completes it bounds the server processing time
 * (`processing`). Returns `null` while the request should keep waiting.
 */
export function resolveConverterAbort(state: ConverterWatchdogState, now: number): ConverterAbortReason {
  if (!state.uploadComplete) {
    return now - state.lastActivityAt > state.stallMs ? 'stall' : null
  }
  return now - state.uploadCompletedAt > state.processingMs ? 'processing' : null
}

// Web counterpart of the pilotapp ChartConverterWebClient. The converter
// (currently only on the pilotapp BFF, reached same-origin via the /pilotapp-bff
// proxy — Vite in dev, nginx in built environments) returns
// a ZIP of {z}/{x}/{y}.png tiles. We unzip it in memory, derive bounds/zoom from
// the tile keys, register the tiles so Mapbox can serve them through the
// `userchart://` protocol, and hand back the tiles URL template.
@injectable()
export class ChartConverterWebClient implements IChartConverterWebClient {
  constructor(@inject(InjectionTokens.HttpClient) private httpClient: IHttpClient) {}

  protected getRemoteUrl(): string {
    // Same-origin path in every environment: dev proxies it to VITE_PILOTAPP_BFF_URL
    // via Vite (vite.config.ts), and built environments proxy it via the frontend
    // nginx (deployment/docker/nginx.conf). Keeping the browser request same-origin
    // lets the server hold the long-lived converter connection, so large uploads are
    // not aborted by the browser with ERR_NETWORK_CHANGED on flaky/multi-homed networks.
    return `${PILOTAPP_BFF_BASE}/chart-converter`
  }

  async convert(request: ConvertChartRequest): Promise<Result<ConvertedChartData>> {
    const { file, chartId, onProgress, signal, skipOriginalPersistence } = request

    // The converter expects a single multipart `file` field (no auth), matching
    // the pilotapp client.
    const formData = new FormData()
    formData.append('file', file)

    const processingTimeoutMs = MBTILES_EXTENSION_REGEX.test(file.name)
      ? CHART_CONVERTER_TIMEOUT_MBTILES_MS
      : CHART_CONVERTER_TIMEOUT_PDF_TIFF_MS

    // Activity-based watchdog instead of one absolute request timeout: a slow but
    // progressing upload (slow 4G) must not be killed just for taking long, while a
    // truly dead connection still aborts promptly. We drive abortion ourselves via an
    // internal controller (chained to the caller's signal) and do not pass `timeout`.
    const controller = new AbortController()
    const onExternalAbort = () => controller.abort()
    if (signal) {
      if (signal.aborted) controller.abort()
      else signal.addEventListener('abort', onExternalAbort)
    }

    const watchdogState: ConverterWatchdogState = {
      uploadComplete: false,
      lastActivityAt: Date.now(),
      uploadCompletedAt: 0,
      stallMs: CHART_CONVERTER_UPLOAD_STALL_TIMEOUT_MS,
      processingMs: processingTimeoutMs
    }
    let abortReason: ConverterAbortReason = null

    const handleUploadProgress = (event: HttpUploadProgressEvent) => {
      watchdogState.lastActivityAt = Date.now()
      if (!watchdogState.uploadComplete && event.total && event.loaded >= event.total) {
        watchdogState.uploadComplete = true
        watchdogState.uploadCompletedAt = watchdogState.lastActivityAt
      }
      onProgress?.(event.loaded, event.total)
    }

    const watchdog = setInterval(() => {
      const reason = resolveConverterAbort(watchdogState, Date.now())
      if (reason) {
        abortReason = reason
        controller.abort()
      }
    }, CHART_CONVERTER_WATCHDOG_INTERVAL_MS)

    let response: HttpResponse<ArrayBuffer>
    try {
      response = await this.httpClient.post<ArrayBuffer>(
        this.getRemoteUrl(),
        formData as unknown as Record<string, any>,
        {
          responseType: 'arraybuffer',
          signal: controller.signal,
          onUploadProgress: handleUploadProgress
        }
      )
    } finally {
      clearInterval(watchdog)
      signal?.removeEventListener('abort', onExternalAbort)
    }

    if (response.success === false) {
      if (signal?.aborted) return Result.fail('ABORTED')
      if (abortReason === 'stall') return Result.fail('CONNECTION_STALLED')
      if (abortReason === 'processing') return Result.fail('CONVERSION_TIMEOUT')
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail(this.parseErrorCode(response.response))
    }

    try {
      const built = await this.buildConvertedChart(chartId, response.response)
      if (built.isSuccess && !skipOriginalPersistence) {
        // Keep the imported source file in the (in-memory) outbox so the next sync
        // uploads it to the server. With the original on the server, a chart whose
        // processed tiles are ever missing can be re-hydrated by re-converting it, and
        // "download original" works on any browser. Stored as a Blob.
        setPendingOriginalUpload(chartId, file, file.name)
      }
      return built
    } catch {
      return Result.fail('CONVERSION_FAILED')
    }
  }

  private async buildConvertedChart(chartId: string, zipBuffer: ArrayBuffer): Promise<Result<ConvertedChartData>> {
    const { tiles, tileKeys } = await extractTilesFromZip(zipBuffer)

    if (tiles.size === 0) return Result.fail('CONVERSION_FAILED')

    const boundsData = calculateBoundsFromTileKeys(tileKeys)
    if (!boundsData) return Result.fail('CONVERSION_FAILED')

    registerUserChartTiles(chartId, tiles)
    // Hold the source ZIP in the (in-memory) outbox so the next sync uploads it to the
    // server (see UserChartSyncService). Once uploaded, the server is the source of
    // truth and any browser downloads the tiles via downloadProcessed; nothing is
    // persisted locally.
    setPendingProcessedUpload(chartId, zipBuffer)

    return Result.ok({
      chartId,
      bounds: boundsData.bounds as UserChartBounds,
      minZoom: boundsData.minZoom,
      maxZoom: boundsData.maxZoom,
      tilesUrlTemplate: userChartTilesUrlTemplate(chartId)
    })
  }

  private parseErrorCode(body: ArrayBuffer | unknown): string {
    try {
      const text = new TextDecoder().decode(body as ArrayBuffer)
      const json = JSON.parse(text) as ChartConverterErrorBody
      if (json?.error) return json.error
    } catch {
      // Non-JSON / non-decodable body — fall through to the generic code.
    }
    return 'CONVERSION_FAILED'
  }
}
